'use client'

import { useEffect, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import {
  CloseButton,
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react'
import {
  ArrowRightIcon,
  BeakerIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentCheckIcon,
  QuestionMarkCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { TrackingEvents } from '@/lib/analytics'
import {
  DIAGNOSTIC_NUDGE_SUPPRESS_EVENT,
  hasReachedDiagnosticNudgeScroll,
  hasSeenDiagnosticNudgeInWindow,
  markDiagnosticNudgeSeenInWindow,
} from '@/lib/diagnostic-nudge'
import { shouldHideDiagnosticToolsLauncher } from './diagnosticToolsVisibility'

const QuickAssessment = dynamic(
  () => import('@/components/shared/QuickAssessment').then((module) => module.QuickAssessment),
  {
    ssr: false,
    loading: () => (
      <p className="rounded-2xl border border-water-border bg-water-canvas p-5 text-sm text-water-muted" role="status">
        Loading the secure assessment form…
      </p>
    ),
  },
)

const NUDGE_DELAY_MS = 3_000
const DESKTOP_NUDGE_QUERY = '(min-width: 1024px)'

export function DiagnosticToolsLauncher() {
  const pathname = usePathname()
  return <DiagnosticToolsLauncherForPath key={pathname} pathname={pathname} />
}

function DiagnosticToolsLauncherForPath({ pathname }) {
  const [isOpen, setIsOpen] = useState(false)
  const [hasOpened, setHasOpened] = useState(false)
  const [isNudgeVisible, setIsNudgeVisible] = useState(false)
  const [isDesktopNudgeViewport, setIsDesktopNudgeViewport] = useState(false)
  const triggerRef = useRef(null)
  const wasOpenRef = useRef(false)
  const hasConsumedNudgeRef = useRef(false)

  const isHidden = shouldHideDiagnosticToolsLauncher(pathname)

  const openTools = (source) => {
    hasConsumedNudgeRef.current = true
    markDiagnosticNudgeSeenInWindow(window)
    setIsNudgeVisible(false)
    setHasOpened(true)
    setIsOpen(true)
    TrackingEvents.trackDiagnosticTools('open', {
      page_path: pathname || '/',
      source,
    })
  }

  const closeTools = () => {
    setIsOpen(false)
  }

  const dismissNudge = () => {
    hasConsumedNudgeRef.current = true
    markDiagnosticNudgeSeenInWindow(window)
    setIsNudgeVisible(false)
    window.requestAnimationFrame(() => triggerRef.current?.focus())
    TrackingEvents.trackDiagnosticTools('nudge_dismiss', {
      page_path: pathname || '/',
    })
  }

  const chooseTool = (tool) => {
    TrackingEvents.trackDiagnosticTools('choice', {
      page_path: pathname || '/',
      tool,
    })
    setIsOpen(false)
  }

  useEffect(() => {
    const mediaQuery = window.matchMedia(DESKTOP_NUDGE_QUERY)
    const updateViewport = () => {
      setIsDesktopNudgeViewport(mediaQuery.matches)
      if (!mediaQuery.matches) setIsNudgeVisible(false)
    }

    updateViewport()
    mediaQuery.addEventListener('change', updateViewport)
    return () => mediaQuery.removeEventListener('change', updateViewport)
  }, [])

  useEffect(() => {
    const suppressNudge = () => {
      hasConsumedNudgeRef.current = true
      setIsNudgeVisible(false)
    }
    window.addEventListener(DIAGNOSTIC_NUDGE_SUPPRESS_EVENT, suppressNudge)
    return () => window.removeEventListener(DIAGNOSTIC_NUDGE_SUPPRESS_EVENT, suppressNudge)
  }, [])

  useEffect(() => {
    if (isHidden || !isDesktopNudgeViewport) return undefined
    if (hasConsumedNudgeRef.current || hasSeenDiagnosticNudgeInWindow(window)) return undefined

    let delayElapsed = false
    let scrollFrame = null
    let scrollThresholdReached = hasReachedDiagnosticNudgeScroll({
      scrollY: window.scrollY,
      scrollHeight: document.documentElement.scrollHeight,
      viewportHeight: window.innerHeight,
    })

    const maybeShowNudge = () => {
      if (!delayElapsed || !scrollThresholdReached) return
      if (!window.matchMedia(DESKTOP_NUDGE_QUERY).matches) return
      if (document.visibilityState !== 'visible') return
      if (document.body.dataset.assessmentDialogOpen === 'true') return
      const hasVisibleDialog = Array.from(document.querySelectorAll('[role="dialog"]'))
        .some((dialog) => dialog.getClientRects().length > 0)
      if (hasVisibleDialog) return
      if (hasConsumedNudgeRef.current || hasSeenDiagnosticNudgeInWindow(window)) return

      hasConsumedNudgeRef.current = true
      markDiagnosticNudgeSeenInWindow(window)
      setIsNudgeVisible(true)
      TrackingEvents.trackDiagnosticTools('nudge_view', {
        page_path: pathname || '/',
        delay_seconds: NUDGE_DELAY_MS / 1000,
        scroll_threshold: 0.3,
      })
      window.removeEventListener('scroll', handleScroll)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }

    const handleScroll = () => {
      if (scrollFrame !== null) return
      scrollFrame = window.requestAnimationFrame(() => {
        scrollFrame = null
        scrollThresholdReached = hasReachedDiagnosticNudgeScroll({
          scrollY: window.scrollY,
          scrollHeight: document.documentElement.scrollHeight,
          viewportHeight: window.innerHeight,
        })
        maybeShowNudge()
      })
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') maybeShowNudge()
    }

    const timer = window.setTimeout(() => {
      delayElapsed = true
      maybeShowNudge()
    }, NUDGE_DELAY_MS)

    window.addEventListener('scroll', handleScroll, { passive: true })
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      window.clearTimeout(timer)
      if (scrollFrame !== null) window.cancelAnimationFrame(scrollFrame)
      window.removeEventListener('scroll', handleScroll)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isDesktopNudgeViewport, isHidden, pathname])

  useEffect(() => {
    if (!isOpen) {
      delete document.body.dataset.diagnosticToolsOpen
      if (!wasOpenRef.current) return undefined
      wasOpenRef.current = false
      const frame = window.requestAnimationFrame(() => triggerRef.current?.focus())
      return () => window.cancelAnimationFrame(frame)
    }

    wasOpenRef.current = true
    document.body.dataset.diagnosticToolsOpen = 'true'
    return () => {
      delete document.body.dataset.diagnosticToolsOpen
    }
  }, [isOpen])

  if (isHidden) return null

  return (
    <>
      {isNudgeVisible && !isOpen ? (
        <aside
          data-diagnostic-tools-nudge
          className="fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] right-4 z-[60] hidden w-[min(22rem,calc(100vw-2rem))] rounded-[1.25rem] border border-water-border bg-white p-5 pr-14 shadow-[0_18px_48px_rgba(11,34,57,0.18)] lg:block"
        >
          <button
            type="button"
            onClick={dismissNudge}
            aria-label="Dismiss water advisor suggestion"
            className="absolute right-2.5 top-2.5 inline-flex h-11 w-11 touch-manipulation items-center justify-center rounded-full text-water-muted transition-colors hover:bg-water-canvas hover:text-water-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2"
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
          <div role="status" aria-live="polite">
            <p className="font-semibold text-water-ink">Need Help Choosing?</p>
            <p className="mt-1 text-sm leading-6 text-water-muted">
              Ask a specialist for a recommendation, or use a free self-guided tool.
            </p>
          </div>
          <button
            type="button"
            onClick={() => openTools('session_nudge')}
            className="mt-4 inline-flex min-h-11 touch-manipulation items-center justify-center gap-2 rounded-xl bg-water-blue px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-water-blue-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2"
          >
            Show My Options
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </aside>
      ) : null}

      <Link
        href="/landings/help-me-choose"
        data-diagnostic-tools-launcher
        onClick={() => {
          markDiagnosticNudgeSeenInWindow(window)
          TrackingEvents.trackDiagnosticTools('choice', {
            page_path: pathname || '/',
            source: 'mobile_launcher_button',
            tool: 'help_me_choose',
          })
        }}
        className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] right-4 z-[60] inline-flex min-h-12 touch-manipulation items-center gap-2 rounded-[0.875rem] border border-water-border bg-white px-4 py-3 text-sm font-semibold text-water-ink shadow-[0_10px_28px_rgba(11,34,57,0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2 sm:hidden"
      >
        <QuestionMarkCircleIcon className="h-5 w-5 text-water-blue" aria-hidden="true" />
        Find My System
      </Link>

      <button
        ref={triggerRef}
        type="button"
        data-diagnostic-tools-launcher
        onClick={() => openTools('launcher_button')}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls="diagnostic-tools-dialog"
        className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] right-4 z-[60] hidden min-h-12 touch-manipulation items-center gap-2 rounded-[0.875rem] border border-water-border bg-white px-4 py-3 text-sm font-semibold text-water-ink shadow-[0_10px_28px_rgba(11,34,57,0.12)] transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:border-water-blue hover:shadow-[0_14px_34px_rgba(11,34,57,0.16)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2 motion-reduce:transform-none sm:inline-flex"
      >
        <QuestionMarkCircleIcon className="h-5 w-5 text-water-blue" aria-hidden="true" />
        <span>Need Help Choosing?</span>
      </button>

      {hasOpened ? (
        <Dialog
          id="diagnostic-tools-dialog"
          open={isOpen}
          onClose={closeTools}
          unmount={false}
          aria-describedby="diagnostic-tools-description"
          className="relative z-[80]"
        >
          <DialogBackdrop className="fixed inset-0 bg-slate-950/55 backdrop-blur-sm" />
          <div className="fixed inset-0 flex w-screen items-end justify-center overflow-y-auto overscroll-contain p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:items-center sm:p-4">
            <DialogPanel className="relative max-h-[calc(100dvh-1.5rem)] w-full max-w-5xl overflow-y-auto overscroll-contain rounded-[1.375rem] border border-water-border bg-white p-5 shadow-[0_24px_70px_rgba(11,34,57,0.24)] sm:max-h-[calc(100dvh-2rem)] sm:p-8">
              <CloseButton
                type="button"
                aria-label="Close water advisor"
                className="absolute right-3 top-3 inline-flex h-11 w-11 touch-manipulation items-center justify-center rounded-full bg-water-canvas text-water-muted transition-colors hover:bg-water-aqua hover:text-water-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2 sm:right-5 sm:top-5"
              >
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </CloseButton>

              <div className="pr-12">
                <p className="text-[0.8125rem] font-semibold uppercase tracking-[0.08em] text-water-blue">Free Water Advisor</p>
                <DialogTitle className="mt-3 max-w-2xl text-balance text-2xl font-semibold tracking-[-0.025em] text-water-ink sm:text-3xl">
                  Not Sure Which Water System Fits?
                </DialogTitle>
                <p id="diagnostic-tools-description" className="mt-3 max-w-2xl text-pretty text-base leading-7 text-water-muted">
                  Ask a specialist for a personal recommendation, or explore your options without sharing contact details.
                </p>
              </div>

              <div className="mt-7 grid gap-5 lg:grid-cols-[minmax(0,1.18fr)_minmax(18rem,0.82fr)]">
                <section aria-labelledby="water-advisor-form-title" className="rounded-[1.25rem] border border-water-border bg-white p-5 sm:p-6">
                  <div className="flex items-start gap-3">
                    <ChatBubbleLeftRightIcon className="mt-0.5 h-6 w-6 shrink-0 text-water-blue" aria-hidden="true" />
                    <div>
                      <h3 id="water-advisor-form-title" className="text-xl font-semibold text-water-ink">Get an Expert Recommendation</h3>
                      <p className="mt-1 text-sm leading-6 text-water-muted">
                        Share your first name and phone number. Add your main water concern if you know it.
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 border-t border-water-border pt-6">
                    <QuickAssessment
                      mode="residential"
                      service="home-system-recommendation"
                      pagePath={pathname || '/'}
                      placement="diagnostic-launcher"
                      ctaVariant="water-advisor-launcher-v1"
                      funnelVariant="water_advisor_launcher"
                      responsiveVerification
                      submitLabel="Request Free Assessment"
                    />
                  </div>

                  <p className="mt-5 border-t border-water-border pt-5 text-sm leading-6 text-water-muted">
                    Need to include household, address, or installation details?{' '}
                    <Link
                      href="/forms/home-assessment"
                      onClick={() => chooseTool('full_home_assessment')}
                      className="font-semibold text-water-blue underline decoration-water-border underline-offset-4 hover:text-water-blue-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2"
                    >
                      Open the full assessment
                    </Link>
                    .
                  </p>
                </section>

                <aside aria-labelledby="self-guided-tools-title" className="rounded-[1.25rem] border border-water-border bg-water-canvas p-5 sm:p-6">
                  <h3 id="self-guided-tools-title" className="text-xl font-semibold text-water-ink">Prefer to Explore First?</h3>
                  <p className="mt-2 text-sm leading-6 text-water-muted">
                    These tools do not ask for contact details.
                  </p>

                  <div className="mt-5 space-y-4">
                    <Link
                      href="/landings/help-me-choose"
                      onClick={() => chooseTool('help_me_choose')}
                      className="group block rounded-2xl border border-sky-200 bg-white p-5 transition-[border-color,box-shadow] hover:border-sky-300 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2"
                    >
                      <ClipboardDocumentCheckIcon className="h-7 w-7 text-sky-700" aria-hidden="true" />
                      <span className="mt-4 block text-lg font-semibold text-water-ink">Help Me Choose</span>
                      <span className="mt-2 block text-sm leading-6 text-water-muted">
                        Answer 4 questions about your water, treatment scope, and household demand.
                      </span>
                      <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-sky-800">
                        Start Guided Selector
                        <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5 motion-reduce:transform-none" aria-hidden="true" />
                      </span>
                    </Link>

                    <Link
                      href="/water-hardness"
                      onClick={() => chooseTool('water_hardness')}
                      className="group block rounded-2xl border border-cyan-200 bg-white p-5 transition-[border-color,box-shadow] hover:border-cyan-300 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2"
                    >
                      <BeakerIcon className="h-7 w-7 text-cyan-700" aria-hidden="true" />
                      <span className="mt-4 block text-lg font-semibold text-water-ink">Check Water Hardness</span>
                      <span className="mt-2 block text-sm leading-6 text-water-muted">
                        Check local hardness context and whether scale points toward softening.
                      </span>
                      <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-cyan-800">
                        Check Hardness
                        <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5 motion-reduce:transform-none" aria-hidden="true" />
                      </span>
                    </Link>
                  </div>
                </aside>
              </div>
            </DialogPanel>
          </div>
        </Dialog>
      ) : null}
    </>
  )
}
